import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency, formatShortDate } from 'tio-ui/format';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import { t } from 'ember-intl';
import {
  PhSparkle,
  PhArrowRight,
  PhCaretDown,
  PhCaretUp,
  PhRocket,
  PhArchive,
} from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
} from 'tio-employee/services/reimagined-data';

interface TasApplicationsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
}

interface TasApplicationsSignature {
  Args: {
    model: TasApplicationsModel;
  };
}

const TERMINAL_STATES = [
  'TAS.ApplicationState.FULFILLED',
  'TAS.ApplicationState.ABANDONED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
];

const ACTION_NEEDED_STATES = [
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.ATTEND',
];

const PAID_APPLICATION_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
]);

const PENDING_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

const REQUESTED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

export default class CareerPathwaysTasApplicationsIndex extends Component<TasApplicationsSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked showHistory = false;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  get activeApplications() {
    return this.applications.filter(
      (app) => app.recordType === 'application' && !TERMINAL_STATES.includes(app.state)
    );
  }

  get historyApplications() {
    return this.applications.filter(
      (app) => app.recordType === 'application' && TERMINAL_STATES.includes(app.state)
    );
  }

  get hasActive() {
    return this.activeApplications.length > 0;
  }

  get hasHistory() {
    return this.historyApplications.length > 0;
  }

  toggleHistory = () => {
    this.showHistory = !this.showHistory;
  };

  isActionNeeded = (state: string) => ACTION_NEEDED_STATES.includes(state);

  applicationAmountInfo = (application: LiveTasApplication) => {
    const isRequestedState = REQUESTED_APPLICATION_STATES.has(application.state);
    const reimbursementApprovedTotal = application.approvedTotal;

    const courseCashApprovedTotal = isRequestedState
      ? (application.courseCashPreApprovedTotal ?? application.courseCashFinalApprovedTotal ?? 0)
      : (application.courseCashFinalApprovedTotal ?? application.courseCashPreApprovedTotal ?? 0);

    const courseCashUsed = application.courseCashUsed ?? 0;
    const courseCashReserved = application.courseCashReserved ?? 0;

    // Only consider it a course cash app if there's an approved amount OR actual usage/reserved amounts
    const isCourseCashApp =
      courseCashApprovedTotal > 0 || courseCashUsed > 0 || courseCashReserved > 0;

    if (application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED') {
      const amount = application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
        courseCashUsed,
        courseCashReserved,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (
      PAID_APPLICATION_STATES.has(application.paymentState) ||
      application.paymentState === 'TAS.ApplicationPaymentState.PAID_IN_PART'
    ) {
      const amount = application.paidTotal || application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
        courseCashUsed,
        courseCashReserved,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (PENDING_APPLICATION_STATES.has(application.state)) {
      const amount = reimbursementApprovedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_pending',
        courseCashUsed,
        courseCashReserved,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (reimbursementApprovedTotal > 0) {
      return {
        reimbursementAmount: reimbursementApprovedTotal,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_approved',
        courseCashUsed,
        courseCashReserved,
        hasCourseCash: isCourseCashApp,
      };
    }

    return null;
  };

  <template>
    <VStack class="gap-6">
      {{! Hero banner }}
      <PageHero @gradient="warm" @rounded="2xl" class="shadow-lg">
        <:default>
          <div class="tio-col-loose md:flex-row md:items-center md:justify-between">
            <div class="max-w-2xl">
              <div class="tio-row-tight tio-text-xs-semibold uppercase tracking-wide text-white/80">
                <PhSparkle class="tio-icon-sm" />
                {{t "tuition_assistance.reimagined.applications.hero_eyebrow"}}
              </div>
              <Heading @level={{1}} class="tio-fg-inverse">
                {{t "tuition_assistance.reimagined.applications.hero_title"}}
              </Heading>
              <Text class="text-white/80">
                {{t "tuition_assistance.reimagined.applications.hero_subtitle"}}
              </Text>
            </div>
            <LinkTo @route="authenticated.tas.dashboard">
              <Button @intent="primary">{{t
                  "tuition_assistance.reimagined.applications.cta_start"
                }}</Button>
            </LinkTo>
          </div>
        </:default>
      </PageHero>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-applications"
      >
        {{! Active Applications Section }}
        <Section class="mb-6">
          <:header>
            <div class="tio-row-tight">
              <PhRocket @size={{20}} class="tio-fg-info" />
              <Heading @level={{2}}>
                {{t "tuition_assistance.reimagined.applications_index.section_active"}}
              </Heading>
              {{#if this.hasActive}}
                <span
                  class="ml-2 rounded-full tio-bg-info-soft px-2.5 py-0.5 tio-text-xs-semibold tio-fg-info-body"
                >
                  {{this.activeApplications.length}}
                </span>
              {{/if}}
            </div>
          </:header>
          <:body>
            {{#if this.hasActive}}
              <div class="grid grid-cols-1 gap-4">
                {{#each this.activeApplications as |application|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                    <div class="tio-row-start-between gap-4">
                      <div class="min-w-0">
                        <Heading @level={{3}}>{{application.name}}</Heading>
                        <Text @variant="muted">{{application.programName}}</Text>
                        {{#if application.coursesBeginDate}}
                          <Text @variant="muted" class="block whitespace-nowrap">
                            {{t
                              "tuition_assistance.reimagined.applications.label_starts"
                              date=(formatShortDate application.coursesBeginDate)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <div class="tio-row-tight flex-wrap">
                        <StatePill @state={{application.state}} />
                        <StatePill @state={{application.paymentState}} />
                      </div>
                    </div>
                    <div class="mt-4 tio-row-loose">
                      {{#let (this.applicationAmountInfo application) as |amountInfo|}}
                        {{#if amountInfo}}
                          <div class="tio-row-tight text-xs tio-fg-disabled">
                            <span class="tio-text-sm-semibold tio-fg-primary">
                              {{formatCurrency amountInfo.reimbursementAmount}}
                            </span>
                            <span>{{t amountInfo.reimbursementLabelKey}}</span>
                            {{#if amountInfo.hasCourseCash}}
                              <span>•</span>
                              <span class="tio-text-sm-semibold tio-fg-primary">
                                {{formatCurrency amountInfo.courseCashUsed}}
                              </span>
                              <span>{{t
                                  "tuition_assistance.reimagined.index.application_amount_used"
                                }}</span>
                              <span>•</span>
                              <span class="tio-text-sm-semibold tio-fg-primary">
                                {{formatCurrency amountInfo.courseCashReserved}}
                              </span>
                              <span>{{t
                                  "tuition_assistance.reimagined.index.application_amount_remaining"
                                }}</span>
                            {{/if}}
                          </div>
                        {{/if}}
                      {{/let}}
                      {{#if (eq application.recordType "application")}}
                        <LinkTo
                          @route="authenticated.reimagined.tas.applications.show"
                          @model={{application.id}}
                          class="tio-copy tio-text-sm-semibold"
                        >
                          {{t "tuition_assistance.reimagined.applications.link_review"}}
                          <PhArrowRight class="tio-icon-sm ml-1 inline" />
                        </LinkTo>
                      {{else}}
                        <LinkTo
                          @route="authenticated.reimagined.tas.programs.show"
                          @model={{application.id}}
                          class="tio-copy tio-text-sm-semibold"
                        >
                          {{t "tuition_assistance.reimagined.index.link_open_program"}}
                          <PhArrowRight class="tio-icon-sm ml-1 inline" />
                        </LinkTo>
                      {{/if}}
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div
                class="rounded-2xl border border-dashed tio-border-strong tio-bg-sunken p-8 text-center"
              >
                <PhRocket @size={{32}} class="mx-auto mb-3 tio-fg-disabled" />
                <Heading @level={{3}}>
                  {{t "tuition_assistance.reimagined.applications_index.active_empty_title"}}
                </Heading>
                <Text @variant="muted" class="mt-1">
                  {{t "tuition_assistance.reimagined.applications_index.active_empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">
                      {{t "tuition_assistance.reimagined.applications.cta_start"}}
                    </Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>

        {{! History Section }}
        {{#if this.hasHistory}}
          <Section>
            <:header>
              <button
                type="button"
                class="tio-row-tight w-full text-left"
                {{on "click" this.toggleHistory}}
              >
                <PhArchive @size={{20}} class="tio-fg-muted" />
                <Heading @level={{2}}>
                  {{t "tuition_assistance.reimagined.applications_index.section_history"}}
                </Heading>
                <span
                  class="ml-2 rounded-full tio-bg-sunken px-2.5 py-0.5 tio-text-xs-semibold tio-fg-secondary"
                >
                  {{this.historyApplications.length}}
                </span>
                <span class="ml-auto">
                  {{#if this.showHistory}}
                    <PhCaretUp @size={{16}} class="tio-fg-muted" />
                  {{else}}
                    <PhCaretDown @size={{16}} class="tio-fg-muted" />
                  {{/if}}
                </span>
              </button>
            </:header>
            <:body>
              {{#if this.showHistory}}
                <div class="grid grid-cols-1 gap-4">
                  {{#each this.historyApplications as |application|}}
                    <div
                      class="rounded-2xl border tio-border-default bg-gray-50/50 p-5 dark:bg-gray-800/50"
                    >
                      <div class="tio-row-start-between gap-4">
                        <div class="min-w-0">
                          <Heading @level={{3}}>{{application.name}}</Heading>
                          <Text @variant="muted">{{application.programName}}</Text>
                        </div>
                        <div class="tio-row-tight">
                          <StatePill @state={{application.state}} />
                          <StatePill @state={{application.paymentState}} />
                        </div>
                      </div>
                      <div class="mt-4 tio-row-between">
                        {{#let (this.applicationAmountInfo application) as |amountInfo|}}
                          {{#if amountInfo}}
                            <div class="tio-row-tight text-xs tio-fg-disabled">
                              <span class="tio-text-sm-semibold tio-fg-primary">
                                {{formatCurrency amountInfo.reimbursementAmount}}
                              </span>
                              <span>{{t amountInfo.reimbursementLabelKey}}</span>
                              {{#if amountInfo.hasCourseCash}}
                                <span>•</span>
                                <span class="tio-text-sm-semibold tio-fg-primary">
                                  {{formatCurrency amountInfo.courseCashUsed}}
                                </span>
                                <span>{{t
                                    "tuition_assistance.reimagined.index.application_amount_used"
                                  }}</span>
                                <span>•</span>
                                <span class="tio-text-sm-semibold tio-fg-primary">
                                  {{formatCurrency amountInfo.courseCashReserved}}
                                </span>
                                <span>{{t
                                    "tuition_assistance.reimagined.index.application_amount_remaining"
                                  }}</span>
                              {{/if}}
                            </div>
                          {{/if}}
                        {{/let}}
                        <LinkTo
                          @route="authenticated.reimagined.tas.applications.show"
                          @model={{application.id}}
                          class="tio-inline-row gap-1 tio-text-sm-semibold tio-fg-secondary hover:tio-fg-primary"
                        >
                          {{t "tuition_assistance.reimagined.applications.link_review"}}
                          <PhArrowRight class="tio-icon-sm" />
                        </LinkTo>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </:body>
          </Section>
        {{/if}}

        {{! Fallback empty state when no apps at all }}
        {{#unless this.applications.length}}
          <div
            class="rounded-2xl border border-dashed tio-border-strong tio-bg-sunken p-8 text-center"
          >
            <Heading @level={{3}}>
              {{t "tuition_assistance.reimagined.applications.empty_title"}}
            </Heading>
            <Text @variant="muted" class="mt-1">
              {{t "tuition_assistance.reimagined.applications.empty_body"}}
            </Text>
            <div class="mt-4">
              <LinkTo @route="authenticated.tas.dashboard">
                <Button @intent="primary">
                  {{t "tuition_assistance.reimagined.applications.empty_cta"}}
                </Button>
              </LinkTo>
            </div>
          </div>
        {{/unless}}
      </DataIndicator>
    </VStack>
  </template>
}
